import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { DEFAULT_COMPANY } from '../../constants/company';
import LineFlatView from '../../components/line/LineFlatView';
import LineIsoView from '../../components/line/LineIsoView';
import { stationCycleProgress, stationPartCount, stationProgram, stationVisual } from '../../line/lineVisuals';

const API_BASE = '/machinesim/api';
const SOCKET_PATH = '/machinesim/socket.io';
// The line this twin was built on: F1603, the Marposs bench, the real programs.
const DEFAULT_LINE_ID = 'LINEA-738';

const chip = (active) => ({
  border: active ? '1px solid var(--cn-gold)' : '1px solid rgba(139,160,181,0.2)',
  background: active ? 'rgba(168,144,96,0.12)' : 'var(--cn-surface)',
  color: active ? 'var(--cn-gold)' : 'var(--cn-muted)',
  borderRadius: 8,
  padding: '7px 12px',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
});

export default function LineTwinPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const company = searchParams.get('company') || DEFAULT_COMPANY;
  const LINE_ID = searchParams.get('line') || DEFAULT_LINE_ID;
  const [lines, setLines] = useState([]);
  const pickLine = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('line', id);
    setSearchParams(next);
  };
  const [topology, setTopology] = useState(null);
  const [state, setState] = useState(null);
  // `?view=iso` makes the view linkable, which the runbook's `?debugIso=1` smoke test assumes: the
  // guides are useless if you still have to click into Isometric by hand first, and a headless
  // screenshot cannot click. An explicit param beats the remembered choice; neither beats the user
  // clicking the toggle afterwards.
  const [view, setView] = useState(() => {
    const q = searchParams.get('view');
    if (q === 'iso' || q === 'map') return q;
    return localStorage.getItem('line-twin-view') || 'map';
  });
  const [debugAnchors, setDebugAnchors] = useState(() => searchParams.get('debugIso') === '1');
  const [showRoute, setShowRoute] = useState(true);
  const [showRobotLinks, setShowRobotLinks] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [liveError, setLiveError] = useState(null);

  useEffect(() => { localStorage.setItem('line-twin-view', view); }, [view]);

  useEffect(() => {
    axios.get(`${API_BASE}/line`).then((res) => setLines(res.data?.lines || [])).catch(() => setLines([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setTopology(null);
    setState(null);
    setSelectedKey(null);
    setError(null);
    setLiveError(null);

    const getTopology = async () => {
      try {
        const res = await axios.get(`${API_BASE}/line/${LINE_ID}?company=${encodeURIComponent(company)}`);
        if (alive) setTopology(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || e?.message || 'Failed to load line topology');
      }
    };

    const getState = async () => {
      try {
        const res = await axios.get(`${API_BASE}/line/${LINE_ID}/state?company=${encodeURIComponent(company)}`);
        if (alive) {
          setState(res.data);
          setLiveError(null);
        }
      } catch (e) {
        if (alive) setLiveError(e?.response?.data?.error || e?.message || 'Live line state unavailable');
      }
    };

    getTopology();
    getState();
    const poll = setInterval(getState, 5000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, [company, LINE_ID]);

  useEffect(() => {
    const socket = io({ path: SOCKET_PATH, transports: ['websocket', 'polling'], reconnection: true });
    // Name the line, so the server pushes this one rather than every line of the company.
    const join = () => socket.emit('joinLine', company, LINE_ID);

    socket.on('connect', () => {
      setConnected(true);
      join();
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('line:state', (next) => {
      if (next?.company !== company || next?.lineId !== LINE_ID) return;
      setState(next);
      setLiveError(null);
    });
    if (socket.connected) join();

    return () => {
      socket.emit('leaveLine');
      socket.disconnect();
    };
  }, [company, LINE_ID]);

  const select = useCallback((key) => setSelectedKey((prev) => prev === key ? null : key), []);

  const selected = useMemo(() => {
    if (!topology || !selectedKey) return null;
    const station = topology.stations?.find((s) => s.key === selectedKey);
    if (station) return { kind: 'station', value: station };
    const node = topology.nodes?.find((n) => n.key === selectedKey);
    if (node) return { kind: 'node', value: node };
    const robot = topology.robots?.find((r) => r.id === selectedKey);
    if (robot) return { kind: 'robot', value: robot };
    return null;
  }, [selectedKey, topology]);

  const routeIntegrity = topology
    ? JSON.stringify(topology.route) === JSON.stringify(topology.routeFromRobotSequences)
    : true;

  if (!topology && !error) {
    return <div style={{ padding: 32, color: 'var(--cn-muted)' }}>Loading line twin…</div>;
  }

  return (
    <div style={{ padding: '24px 28px 32px', minHeight: '100%', background: 'var(--cn-bg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: 'var(--cn-mono, monospace)', fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--cn-gold)', marginBottom: 5 }}>
            {company} · {LINE_ID}
          </div>
          <h1 style={{ margin: 0, color: 'var(--cn-text)', fontSize: 26 }}>Line Twin</h1>
          {topology && (
            <div style={{ color: 'var(--cn-muted)', marginTop: 5, fontSize: 12 }}>
              {topology.name} · part {topology.part ?? topology.model}
              {topology.source === 'transferred' && ' · cells and times carried over from Línea 750 (no balance sheet for this line)'}
            </div>
          )}
          {lines.length > 1 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {lines.map((l) => (
                <button key={l.id} style={chip(l.id === LINE_ID)} onClick={() => pickLine(l.id)} title={`${l.name} · ${l.part}`}>
                  {l.erpCode ?? l.id}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div title={connected ? 'Receiving line:state at ~1 Hz' : 'Using REST polling fallback'} style={{ display: 'flex', gap: 6, alignItems: 'center', color: connected ? 'var(--cn-success)' : 'var(--cn-muted)', fontSize: 11, marginRight: 8 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor' }} />
            {connected ? 'Live' : 'Polling'}
          </div>
          <button style={chip(showRoute)} onClick={() => setShowRoute(v => !v)} title="Solid gold: the order a part actually visits the stations">Part route</button>
          <button style={chip(showRobotLinks)} onClick={() => setShowRobotLinks(v => !v)} title="Dashed: each robot's handling circuit. Off by default — two circuits plus the route is more ink than a floor needs">Robot circuits</button>
          <span style={{ width: 1, height: 20, background: 'rgba(168,144,96,0.25)', margin: '0 2px' }} />
          <button style={chip(view === 'map')} onClick={() => setView('map')}>Flat map</button>
          <button style={chip(view === 'iso')} onClick={() => setView('iso')}>Isometric</button>
          {view === 'iso' && (
            <button
              style={chip(debugAnchors)}
              onClick={() => setDebugAnchors((v) => !v)}
              title="Show the logical floor-contact point used to align every machine sprite"
            >
              Alignment guides
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ color: '#e8a89f', background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {topology && liveError && (
        <div style={{ color: 'var(--cn-warning)', background: 'rgba(183,121,31,0.10)', border: '1px solid rgba(183,121,31,0.3)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12 }}>
          Live state temporarily unavailable — showing the cached topology without inventing machine or robot state. {liveError}
        </div>
      )}

      {topology && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Metric label="Planned rate" value={`${state?.rate?.plannedPiecesPerHour ?? topology.balance?.piecesPerHour ?? '—'} pcs/h`} />
            <Metric label="Constraint" value={state?.rate?.constraint || topology.balance?.constraint || '—'} accent />
            <Metric label="Cycle" value={`${topology.balance?.cycleSec?.toFixed?.(1) ?? topology.balance?.cycleSec ?? '—'} s`} />
            <Metric label="Stations" value={topology.stations?.length ?? 0} />
            <Metric label="Cells" value={topology.cells?.length ?? 0} />
            <Metric label="Route cross-check" value={routeIntegrity ? 'MATCH' : 'MISMATCH'} danger={!routeIntegrity} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(0,1fr) 300px' : 'minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
            <div style={{ background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)', borderRadius: 12, padding: view === 'map' ? 10 : 0, overflow: 'hidden' }}>
              {view === 'map'
                ? <LineFlatView topology={topology} state={state} selectedKey={selectedKey} onSelect={select}
                                showRoute={showRoute} showRobotLinks={showRobotLinks} />
                : <LineIsoView topology={topology} state={state} selectedKey={selectedKey} onSelect={select}
                               debugAnchors={debugAnchors} showRoute={showRoute} showRobotLinks={showRobotLinks} />}
            </div>

            {selected && (
              <SelectionPanel selected={selected} topology={topology} state={state} company={company} onClose={() => setSelectedKey(null)} />
            )}
          </div>

          <div style={{ marginTop: 12, color: 'var(--cn-dimmed)', fontSize: 10.5, lineHeight: 1.5 }}>
            Solid gold = part route. Dashed links = robot handling circuit. Robot motion is intentionally static until the engine reports <code>modelled: true</code>.
            {topology?.layoutSource === 'derived' && (
              <>
                {' '}<strong style={{ color: 'var(--cn-gold)' }}>Arrangement is illustrative:</strong> station positions are derived from
                the robot sequence, not surveyed. Cells, membership, sequence and route are the customer&apos;s; the
                geometry is ours until a plan view arrives.
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, accent, danger }) {
  const color = danger ? 'var(--cn-danger)' : accent ? 'var(--cn-gold)' : 'var(--cn-text)';
  return (
    <div style={{ background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)', borderRadius: 8, padding: '8px 11px', minWidth: 108 }}>
      <div style={{ color: 'var(--cn-dimmed)', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <div style={{ color, fontSize: 13, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function SelectionPanel({ selected, topology, state, company, onClose }) {
  const close = (
    <button onClick={onClose} aria-label="Close details" style={{ border: 0, background: 'transparent', color: 'var(--cn-muted)', fontSize: 18, cursor: 'pointer' }}>×</button>
  );

  if (selected.kind === 'station') {
    const station = selected.value;
    const stationState = state?.stations?.[station.key];
    const visual = stationVisual(stationState);
    const progress = stationCycleProgress(stationState);
    const program = stationProgram(stationState);
    const wip = state?.wip?.[station.key] ?? 0;
    return (
      <aside style={panelStyle}>
        <div style={panelHeader}><div><Eyebrow>{station.machineType}</Eyebrow><strong style={{ fontSize: 20 }}>{station.key}</strong></div>{close}</div>
        <StatusPill visual={visual} />
        <Row label="Machine" value={station.machineName} />
        <Row label="Cell" value={station.cellId} />
        <Row label="Cycle" value={`${station.cycleSec.toFixed(1)} s`} />
        <Row label="Rated" value={`${station.piecesPerHour} pcs/h`} />
        <Row label="Parts" value={stationPartCount(stationState)} />
        <Row label="WIP after station" value={wip} />
        {program && <Row label="Program" value={program} />}
        {progress !== null && <Row label="Cycle progress" value={`${Math.round(progress)}%`} />}
        {visual.alarm && (
          <div style={{ marginTop: 12, background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.35)', borderRadius: 7, padding: 10, color: '#e8a89f', fontSize: 11 }}>
            <strong>{visual.alarm.code}</strong>{visual.alarm.message ? ` · ${visual.alarm.message}` : ''}
          </div>
        )}
        <Link
          to={{ pathname: `/machine/${station.machineName}`, search: `?company=${company}` }}
          style={{ display: 'block', marginTop: 16, textAlign: 'center', textDecoration: 'none', borderRadius: 7, padding: '9px 10px', background: 'var(--cn-gold)', color: '#1a1206', fontWeight: 800, fontSize: 11 }}
        >
          Open machine →
        </Link>
      </aside>
    );
  }

  if (selected.kind === 'robot') {
    const robot = selected.value;
    const live = state?.robots?.[robot.id];
    const constrained = topology.balance?.constraint === robot.id;
    return (
      <aside style={panelStyle}>
        <div style={panelHeader}><div><Eyebrow>Robot resource</Eyebrow><strong style={{ fontSize: 20 }}>{robot.id}</strong></div>{close}</div>
        {constrained && <div style={{ color: 'var(--cn-gold)', fontWeight: 800, fontSize: 11, marginBottom: 12 }}>LINE CONSTRAINT</div>}
        <Row label="Cell" value={robot.cellId} />
        <Row label="Round time" value={`${robot.roundTimeSec.toFixed(1)} s`} />
        <Row label="Effective cycle" value={`${robot.cycleSec.toFixed(1)} s`} />
        <Row label="Rated" value={`${robot.piecesPerHour} pcs/h`} />
        <Row label="Handled points" value={robot.moveCount} />
        <Row label="Live model" value={live?.modelled ? 'Available' : 'Not modelled yet'} />
        {!live?.modelled && (
          <div style={{ marginTop: 12, color: 'var(--cn-muted)', fontSize: 11, lineHeight: 1.5 }}>
            Topology and timing are real. Position, holding state and utilisation are intentionally not animated until the engine publishes them.
          </div>
        )}
      </aside>
    );
  }

  const node = selected.value;
  return (
    <aside style={panelStyle}>
      <div style={panelHeader}><div><Eyebrow>{node.kind}</Eyebrow><strong style={{ fontSize: 16 }}>{node.name}</strong></div>{close}</div>
      <Row label="Key" value={node.key} />
      <Row label="Cell" value={node.cellId} />
      {node.handsOffTo && <Row label="Hands off to" value={node.handsOffTo} />}
      {node.exit && <Row label="Line exit" value="Yes" />}
      <div style={{ marginTop: 12, color: 'var(--cn-muted)', fontSize: 11, lineHeight: 1.5 }}>
        This is a handled point from the robot program, not a dispatchable machine.
      </div>
    </aside>
  );
}

const panelStyle = {
  background: 'var(--cn-surface)',
  border: '1px solid rgba(168,144,96,0.16)',
  borderRadius: 12,
  padding: 16,
  color: 'var(--cn-text)',
  position: 'sticky',
  top: 16,
};

const panelHeader = { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 };

function Eyebrow({ children }) {
  return <div style={{ color: 'var(--cn-dimmed)', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>{children}</div>;
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(139,160,181,0.08)', fontSize: 10.5 }}>
      <span style={{ color: 'var(--cn-muted)' }}>{label}</span>
      <span style={{ color: 'var(--cn-text)', fontWeight: 700, textAlign: 'right', overflowWrap: 'anywhere' }}>{String(value ?? '—')}</span>
    </div>
  );
}

function StatusPill({ visual }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${visual.hex}`, borderRadius: 999, padding: '4px 8px', color: visual.hex, fontSize: 9, fontWeight: 900, marginBottom: 12 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: visual.hex }} />
      {visual.label}
    </div>
  );
}
